// ============================================================================
// ReportRunner.jsx
// The report screen shared by Soft-Pak Reports and Fleet Reports. Each reads live
// through its own reporting API. The list of reports and each report's settings
// come from that API's catalog, so a report added there appears here without a
// portal change.
//
// Exports are built on the server (<basePath>/:id/export.xlsx) rather than from
// the rows on screen, so the access log records every one with its filter.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Database, Download, Play, ShieldAlert } from 'lucide-react';
import { api } from '../../services/api';
import { c, t, card, btn, tbl } from '../../theme';
import LoadingSpinner from '../Shared/LoadingSpinner';
import PageHeader from '../Shared/PageHeader';

// Crystal's {?WeekEnd} is a week-ending date: offer the last Sunday.
function lastSunday() {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay());
  return d.toLocaleDateString('en-CA'); // YYYY-MM-DD in local time
}
function today() {
  return new Date().toLocaleDateString('en-CA');
}
function firstOfMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toLocaleDateString('en-CA');
}

const DEFAULTS = {
  week_end: lastSunday,
  action_date: () => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toLocaleDateString('en-CA'); },
  from: firstOfMonth,
  to: today,
};

const label = (name) => name.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

// Numbers arrive as strings (exact decimals); show them right-aligned.
const isNumeric = (v) => typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v);

function defaultsFor(report) {
  const out = {};
  for (const p of report?.params || []) {
    if (DEFAULTS[p.name]) out[p.name] = DEFAULTS[p.name]();
  }
  return out;
}

export default function ReportRunner({ basePath, eyebrow, title, subtitle, filePrefix }) {
  const catalog = useQuery({
    queryKey: [basePath, 'catalog'],
    queryFn: () => api.get(`${basePath}/`),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  // listed false: a report a page reads for itself, not one to run by hand.
  const reports = useMemo(() => (catalog.data?.reports || []).filter((r) => r.listed !== false), [catalog.data]);

  const [selectedId, setSelectedId] = useState(null);
  const selected = useMemo(() => reports.find((r) => r.id === selectedId) || reports[0] || null, [reports, selectedId]);
  const [values, setValues] = useState({});
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  // A different report starts from its own defaults and an empty result.
  // `selected` is memoized on the catalog, so this runs when the choice changes,
  // not on every render.
  useEffect(() => {
    setValues(defaultsFor(selected));
    setResult(null);
    setError(null);
  }, [selected]);

  const query = useMemo(() => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(values)) if (v) qs.set(k, v);
    return qs.toString();
  }, [values]);

  async function run() {
    setRunning(true);
    setError(null);
    try {
      setResult(await api.get(`${basePath}/${selected.id}${query ? `?${query}` : ''}`, { timeout: 60000 }));
    } catch (e) {
      setResult(null);
      setError(e.data?.problems?.length ? `${e.message} ${e.data.problems.join('; ')}` : e.message);
    } finally {
      setRunning(false);
    }
  }

  async function doExport() {
    setExporting(true);
    try {
      const blob = await api.getBlob(`${basePath}/${selected.id}/export.xlsx${query ? `?${query}` : ''}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filePrefix}-${selected.id}-${today()}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Export failed.');
    } finally {
      setExporting(false);
    }
  }

  if (catalog.isLoading) return <LoadingSpinner />;
  if (catalog.isError) {
    return (
      <div>
        <PageHeader eyebrow={eyebrow} title={title} />
        <div style={{ ...card.base, padding: 20, color: c.dangerText, background: c.dangerTint }}>
          {catalog.error.message}
        </div>
      </div>
    );
  }

  const columns = result?.rows?.length ? Object.keys(result.rows[0]) : [];

  return (
    <div>
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        subtitle={subtitle}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) 1fr', gap: 16, alignItems: 'start' }}>
        <div style={{ ...card.base, padding: 8 }}>
          {reports.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setSelectedId(r.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                padding: '10px 12px', border: 'none', borderRadius: 8, cursor: 'pointer',
                background: selected?.id === r.id ? c.infoTint : 'transparent',
                color: selected?.id === r.id ? c.info : c.textStrong,
                fontSize: 13, fontWeight: selected?.id === r.id ? 600 : 500, fontFamily: t.family,
              }}
            >
              <Database size={14} />
              <span style={{ flex: 1 }}>{r.title}</span>
              {r.personalData && <ShieldAlert size={13} color={c.warning} aria-label="Contains customer personal data" />}
            </button>
          ))}
        </div>

        {selected && (
          <div style={{ display: 'grid', gap: 16 }}>
            <div style={{ ...card.base }}>
              <div style={card.head}>{selected.title}</div>
              <div style={{ padding: 16, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
                {selected.params.map((p) => (
                  <label key={p.name} style={{ display: 'grid', gap: 4, fontSize: 12, color: c.textMuted }}>
                    {label(p.name)}{p.required ? ' *' : ''}
                    {p.type === 'oneOf' ? (
                      <select
                        value={values[p.name] || ''}
                        onChange={(e) => setValues({ ...values, [p.name]: e.target.value })}
                        style={inputStyle}
                      >
                        {/* A required list starts on "Select…": the browser would otherwise show the
                            first value while nothing is chosen, and the API refuses the run. */}
                        {p.required ? <option value="" disabled>Select…</option> : <option value="">All</option>}
                        {p.values.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    ) : (
                      <input
                        type={p.type === 'date' ? 'date' : 'text'}
                        maxLength={p.type === 'company' ? 2 : undefined}
                        placeholder={p.type === 'company' ? (p.required ? 'e.g. LP' : 'All') : undefined}
                        value={values[p.name] || ''}
                        onChange={(e) => setValues({ ...values, [p.name]: e.target.value })}
                        style={{ ...inputStyle, width: p.type === 'company' ? 70 : undefined }}
                      />
                    )}
                  </label>
                ))}
                <button type="button" onClick={run} disabled={running} style={primaryButton}>
                  <Play size={14} /> {running ? 'Running…' : 'Run'}
                </button>
                <button type="button" onClick={doExport} disabled={exporting || running} style={secondaryButton}>
                  <Download size={14} /> {exporting ? 'Exporting…' : 'Export to Excel'}
                </button>
              </div>
              {selected.personalData && (
                <div style={{ padding: '0 16px 14px', fontSize: 12, color: c.warningText }}>
                  Contains customer names, addresses and phone numbers. Exports are recorded in the access log.
                </div>
              )}
            </div>

            {error && (
              <div style={{ ...card.base, padding: 14, color: c.dangerText, background: c.dangerTint, fontSize: 13 }}>{error}</div>
            )}

            {result && (
              <div style={{ ...card.base, overflow: 'hidden' }}>
                <div style={{ ...card.head, display: 'flex', justifyContent: 'space-between' }}>
                  <span>{result.rows.length.toLocaleString()} rows</span>
                  {result.truncated && <span style={{ color: c.warningText }}>Showing the first 50,000 rows. Narrow the settings to see all.</span>}
                </div>
                {result.rows.length === 0 ? (
                  <div style={{ padding: 20, fontSize: 13, color: c.textMuted }}>No rows for these settings.</div>
                ) : (
                  <div style={{ overflow: 'auto', maxHeight: '65vh' }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
                      <thead>
                        <tr>
                          {columns.map((col) => (
                            <th key={col} style={{ ...tbl.thSm, position: 'sticky', top: 0, background: c.surfaceSunk, whiteSpace: 'nowrap' }}>
                              {label(col)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {result.rows.slice(0, 2000).map((row, i) => (
                          <tr key={i} style={tbl.tr}>
                            {columns.map((col) => (
                              <td key={col} style={{ ...(isNumeric(row[col]) ? tbl.tdRSm : tbl.tdSm), whiteSpace: 'nowrap' }}>
                                {row[col] ?? ''}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {result.rows.length > 2000 && (
                      <div style={{ padding: 12, fontSize: 12, color: c.textMuted }}>
                        The first 2,000 rows are shown here; Export to Excel has all {result.rows.length.toLocaleString()}.
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  padding: '7px 9px', border: `1px solid ${c.borderStrong}`, borderRadius: 6,
  fontSize: 13, color: c.text, background: c.surface, fontFamily: t.family,
};
const primaryButton = { ...btn.primary, display: 'inline-flex', alignItems: 'center', gap: 6 };
const secondaryButton = { ...btn.secondary, display: 'inline-flex', alignItems: 'center', gap: 6 };
